import {useQuery} from "@tanstack/react-query";
import {getDocs} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";

import {type QueryOptions, buildQuery} from "../queryUtils";

export const queryHookFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return ({
    key,
    ...options
  }: {key: CollectionArgs} & QueryOptions<z.infer<T>>) => {
    return useQuery<z.infer<T>[]>({
      queryKey: [...config.getCollectionPath(key), JSON.stringify(options)],
      queryFn: async () => {
        try {
          const q = buildQuery<z.infer<T>>(
            config.getCollectionPath(key),
            options,
          );
          const querySnapshot = await getDocs(q);
          const data = querySnapshot.docs.map((doc) =>
            config.parseData(doc.id, doc.data()),
          );
          return data;
        } catch (err) {
          console.error(
            `Error fetching collection [${config.getCollectionPath(key)}]: ${JSON.stringify(err)}`,
            err,
          );
          throw err;
        }
      },
      enabled: !Object.values(key).some((segment) => !segment),
      staleTime: Number.POSITIVE_INFINITY,
    });
  };
};
